import { HitsPerPage } from 'react-instantsearch';
import dynamic from 'next/dynamic';

import MapPin from '@/icons/light/map-pin.svg';
import { RichInline } from '@/atoms';
import { Hero, HomeParaguas } from '@/molecules';
import { HeroBanner } from '@/molecules/hero/variants/HeroBanner';
import { HeroCentered } from '@/molecules/hero/variants/HeroCentered';

// Imports ESTÁTICOS a propósito: con `next/dynamic` (aun con ssr:true) el
// subárbol se hidrata vacío hasta que llega el chunk — el buscador del hero
// SSR desaparecía y reaparecía, saltando todo el layout de debajo. `dynamic`
// se mantiene sólo para los iconos de las cards (import con template string).
import InstantSearch from '@/components/algolia/instant-search';
import SearchBoxDesktop from '@/components/algolia/search-box-desktop';
import { isBotonContacto, useBotonContacto } from '@/hooks/useBotonContacto';
import { textStyleClass } from '@/utils';
import { getRealUrl } from '@/utils/utils';

import type { TypeCard as TypeCardHomeParaguas } from '@/molecules/home-paraguas/types';

/**
 * ComponentRevisadasHero
 */
export const ComponentRevisadasHero = ({
  Titulo,
  Subtitulo,
  Descripcion,
  TituloEstilo,
  SubtituloEstilo,
  DescripcionEstilo,
  ImagenFondo,
  videoFondo,
  videoFondoMovil,
  ListItem,
  InstantSearchSettings,
  ColorFondo,
  Boton,
  Overlay,
  Tipo,
  CardLinks,
  TituloCards,
  DescripcionCards,
  CurvaInferior,
  OcultarLogo,
  Trending,
  PosicionImagen,
}: any) => {
  const { modal, abrirContacto } = useBotonContacto();
  const tituloStyleClass = textStyleClass(TituloEstilo);
  const subtituloStyleClass = textStyleClass(SubtituloEstilo);
  const descripcionStyleClass = textStyleClass(DescripcionEstilo);
  // Punto focal del recorte de `ImagenFondo` (igual que en ContenidoConImagen):
  // dos % (X/Y, 0–100) → string CSS `object-position`. Entradas antiguas sin
  // el componente se quedan en `undefined` → `object-position: center` por defecto.
  const imagePosition =
    Number.isFinite(PosicionImagen?.X) && Number.isFinite(PosicionImagen?.Y)
      ? `${PosicionImagen.X}% ${PosicionImagen.Y}%`
      : undefined;
  if (Tipo === 'Centered') {
    return (
      <HeroCentered
        backgroundImage={ImagenFondo}
        eyebrow={Subtitulo}
        title={Titulo}
        overlay={Overlay ?? 0.4}
        instantSearchSettings={InstantSearchSettings}
        trending={Trending}
      />
    );
  }
  if (Tipo === 'Banner') {
    // Banner corto para páginas de cabecera (Sobre Nosotros, Servicios, etc.).
    // Compatible con `transparentMenu` — el header flota encima y el contenido
    // queda centrado por debajo gracias al `pt-(--header-height)` interno.
    return (
      <HeroBanner
        backgroundImage={ImagenFondo}
        imagePosition={imagePosition}
        title={Titulo}
        subtitle={Subtitulo}
        description={Descripcion}
        tituloStyleClass={tituloStyleClass}
        subtituloStyleClass={subtituloStyleClass}
      />
    );
  }

  return (
    <div
      className={
        InstantSearchSettings
          ? 'relative'
          : `overflow-hidden${CurvaInferior ? 'hero-mask' : ''}`
      }
    >
      <Hero
        backgroundImage={
          ImagenFondo?.url ? getRealUrl(ImagenFondo.url) : undefined
        }
        backgroundVideo={{
          desktop: videoFondo?.map((itm: any) => ({
            src: getRealUrl(itm?.url),
            type: itm?.mime,
          })),
          mobile: videoFondoMovil?.map((itm: any) => ({
            src: getRealUrl(itm?.url),
            type: itm?.mime,
          })),
        }}
        backgroundColor={ColorFondo == 'Negro' ? 'bg-primary-900' : undefined}
        className={
          Tipo == 'Home'
            ? `${
                InstantSearchSettings ? 'mb-[130px] md:mb-[120px]' : ''
              } h-[370px] md:h-[500px]`
            : Tipo?.includes('Landing')
              ? 'h-[445px] md:h-[580px]'
              : undefined
        }
        justify={
          Tipo == 'Home'
            ? 'md:justify-center'
            : Tipo?.includes('Landing')
              ? 'justify-center'
              : 'lg:justify-center'
        }
        variant={
          Tipo?.includes('Landing')
            ? 'default'
            : Tipo == 'Home'
              ? undefined
              : 'blog'
        }
        notLazy
        hideLogo={!!OcultarLogo}
        overlay={Overlay}
        align={
          Tipo == 'LandingDerecha'
            ? 'items-end'
            : Tipo == 'LandingIzquierda'
              ? 'items-start'
              : undefined
        }
        textAlign={
          Tipo == 'LandingDerecha'
            ? 'text-end'
            : Tipo == 'LandingIzquierda'
              ? 'text-start'
              : undefined
        }
      >
        <Hero.Title
          as="h1"
          className="mt-6 md:mt-0"
          typographyClass={tituloStyleClass}
        >
          <RichInline html={Titulo} />
        </Hero.Title>
        {Subtitulo && (
          <Hero.Subtitle
            as="h2"
            className={
              Tipo == 'LandingDerecha'
                ? 'mb-0! flex justify-end gap-2'
                : Tipo == 'LandingIzquierda'
                  ? 'flex gap-2'
                  : undefined
            }
            typographyClass={subtituloStyleClass}
          >
            {Subtitulo}
            <MapPin width={30} />
          </Hero.Subtitle>
        )}
        {Descripcion && (
          <Hero.Description typographyClass={descripcionStyleClass}>
            <RichInline
              as="div"
              html={Descripcion}
              className="[&_p]:m-0 [&_p:not(:last-child)]:mb-3"
            />
          </Hero.Description>
        )}
        {Boton?.[0] && (
          <Hero.Button
            {...(isBotonContacto(Boton[0])
              ? { onClick: () => abrirContacto(Boton[0]) }
              : { href: Boton[0].Link || '#' })}
            size={Boton[0].Size}
            variant={Boton[0].Variante}
          >
            {Boton[0].Texto}
          </Hero.Button>
        )}
        {isBotonContacto(Boton?.[0]) && modal}
        {ListItem.length !== 0 && (
          <Hero.List items={ListItem.map((itm: any) => itm.Item)} />
        )}
      </Hero>

      {InstantSearchSettings ? (
        <InstantSearch
          indexName={`development_api::zfm-disponiblidad.${InstantSearchSettings.Indice.replaceAll(
            '_',
            '-',
          )}`}
          ssrPlaceholder
        >
          <SearchBoxDesktop
            placeholder={InstantSearchSettings.TextoBuscador}
            className="absolute! top-full right-0 left-0 z-10 -translate-y-1/2"
            typeFlat={InstantSearchSettings.TipoVivienda}
          />

          <HitsPerPage
            items={[
              {
                label: '',
                value: InstantSearchSettings.NumeroResultados,
                default: true,
              },
            ]}
            className="hidden"
          />
        </InstantSearch>
      ) : null}

      {InstantSearchSettings === null && CardLinks && CardLinks.length > 0 ? (
        <div className="w-full">
          <HomeParaguas
            className="u-wrapper lg:relative lg:-top-11 lg:z-50"
            title={TituloCards}
            desc={DescripcionCards}
            cards={
              CardLinks?.map((card: any) => ({
                Icon: dynamic(
                  () =>
                    import(
                      `@/icons/duotone/${card.Icono.replaceAll('_', '-')}.svg`
                    ),
                ),
                title: card.Titulo,
                desc: card.Descripcion,
                link: {
                  href: card.UrlLink,
                  label: card.TituloLink,
                },
                theme: card.Tema,
              })) as TypeCardHomeParaguas[]
            }
          />
        </div>
      ) : null}
    </div>
  );
};

ComponentRevisadasHero.displayName = 'ComponentRevisadasHero';
